import { call } from '@advanza/api'
import { useCurrentCountry } from 'hooks/miscHooks'
import { useEffect, useRef, useState } from 'react'
import type { SearchFilters, SearchParams, SearchResponse } from './types'

const DEBOUNCE_MS = 300

interface MatchingDebuggerState {
    results: SearchResponse | null
    isLoading: boolean
    timer: number
}

export function useMatchingDebugger(params: Partial<SearchParams>) {
    const [state, setState] = useState<MatchingDebuggerState>({
        results: null,
        isLoading: false,
        timer: 0,
    })
    const countryCode = useCurrentCountry()
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()

    useEffect(() => {
        if (!params.service) return

        // Mark loading immediately so the UI disables controls right away
        setState((prev) => ({ ...prev, isLoading: true }))

        // Debounce the actual fetch so rapid filter clicks batch into one request
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            const start = Date.now()

            const filters: SearchFilters = { ...params.filters }
            const queryParams: Record<string, unknown> = {
                countryCode,
                limit: params.limit || 10,
                useMultiplierMatchMore: 'true',
                filters: btoa(JSON.stringify(filters)),
            }

            if (params._mode === 'wizard') {
                queryParams.isRequestForm = 1
            } else if (params._mode === 'results') {
                filters._simulateMatches = true
                queryParams.filters = btoa(JSON.stringify(filters))
            }

            if (params._order) {
                queryParams._order = params._order
            }

            const city = params.city || 'nederland'
            call(`search-engine/search/${params.service}/${city}/1/`, {
                query: queryParams,
            })
                .then((response: SearchResponse) => {
                    let order = response.order
                    if (!order && response.sql) {
                        const match = response.sql.match(/.*\bORDER BY\b(.+?)LIMIT/)
                        if (match?.[1]) {
                            order = match[1]
                                .trim()
                                .split(',')
                                .map((p: string) => p.trim())
                        }
                    }
                    // Attach the matched subscription id to each provider so the per-service soft
                    // daily limit rule can check the subscription the pro is matched with.
                    const matchedSubscriptions = response.matchedSubscriptions ?? {}
                    const providers = (response.providers ?? []).map((provider) => ({
                        ...provider,
                        matched_subscription_id:
                            matchedSubscriptions[provider.service_provider_id]?.subscription_id,
                    }))
                    setState({
                        results: { ...response, order, providers },
                        isLoading: false,
                        timer: Date.now() - start,
                    })
                })
                .catch(() => {
                    setState((prev) => ({ ...prev, isLoading: false }))
                })
        }, DEBOUNCE_MS)

        return () => clearTimeout(debounceRef.current)
    }, [
        params.service,
        params.city,
        params._mode,
        params.limit,
        params._order,
        params.filters,
        countryCode,
    ])

    return state
}
